import React, { useEffect, useRef } from "react";
import { ImageDetailsList, ImageListItem } from "./compare-images/image-details-list";
import { CompareGallery } from "./zoom-compare-images/compare-gallery";
import { NO_IMAGE_URL, PRODUCT_MATCH_STEPS } from "../lib/constants";
import { useImageCompare } from "../hooks/use-image-compare";
import { ProductQuickCompare } from "./product-quick-compare";
import { DefaultQuickCompare } from "./default-quick-compare";

export const ZoomCompareImages = ({
    intakeProductId,
    productShots,
    selectedProductCompare,
    onStepChange,
    reference,
    productMatches,
    onZoomCompare,
    showSingleDisplay,
    isDuplicateStaging,
    isPhysicalRemote,
}) => {
    const modalRef = useRef();
    const {
        intakeProducts,
        cxhMatches,
        duplicateStageMatches,
        selectedImages,
        selectImage,
        checkCodingImageSelect,
        quickCompare,
        createPlaceholder,
    } = useImageCompare(intakeProductId, productShots, selectedProductCompare, isDuplicateStaging);

    useEffect(() => {
        modalRef.current.offsetParent.scrollIntoView({ block: "start" });
    }, []);

    const [selectedIntakeProduct, selectedCxhMatch] = selectedImages;

    const imageList = isDuplicateStaging ? duplicateStageMatches : cxhMatches;

    const fullUpc = selectedProductCompare.modifier ? `${selectedProductCompare.upc}-${selectedProductCompare.modifier}` : selectedProductCompare.upc;

    const galleryTitle = isDuplicateStaging
        ? `Product Match ${productMatches.indexOf(selectedProductCompare) + 1} (${fullUpc})`
        : "Possible CES Match";

    const findSelectedImage = (products, name) => {
        const product = products?.find((p) => checkCodingImageSelect(p) === name);

        if (product) {
            return product;
        }

        return createPlaceholder(null, NO_IMAGE_URL, name);
    };

    const addImageDescription = (product) => {
        if (product.variation.editedImageUris) {
            return " (Edited)";
        }

        if (product.variation.uneditedImageUris) {
            return " (Unedited)";
        }

        return null;
    };

    return (
        <div className="image-compare">
            <div ref={modalRef}></div>
            <div className="compare-header">
                <button
                    className="button"
                    onClick={() => onStepChange(PRODUCT_MATCH_STEPS.compare)}
                >
                    Back
                </button>
                <div className="compare-subheader">
                    <h3>Zoom to Compare</h3>
                </div>
                <button className="button -primary -hidden">Save</button>
            </div>
            <div className="quick-compare">
                <h4>Quick compare:</h4>
                {isDuplicateStaging ? (
                    <ProductQuickCompare
                        productMatches={productMatches}
                        onZoomCompare={onZoomCompare}
                        selectedProductCompare={selectedProductCompare}
                    />
                ) : (
                    <DefaultQuickCompare quickCompare={quickCompare} />
                )}
            </div>

            <ImageDetailsList className="images-details-list product-images -zoom">
                {!showSingleDisplay && (
                    <ImageListItem className={`zoom-item ${isDuplicateStaging ? "gray-box" : ""}`}>
                        <CompareGallery
                            title={isDuplicateStaging ? `${isPhysicalRemote ? "Physical" : "Digital"} Product In Hand` : "Intake Product"}
                            selectedProduct={findSelectedImage(intakeProducts, selectedIntakeProduct)}
                            shipmentKey={reference?.shipmentKey}
                            shipmentProductKey={reference?.shipmentProductKey}
                            photographyWipKey={reference?.photographyWipKey}
                        >
                            {intakeProducts.map((product, shotIndex) => {
                                const selected = checkCodingImageSelect(product) === selectedIntakeProduct;

                                return (
                                    <ImageListItem
                                        key={checkCodingImageSelect(product) || shotIndex}
                                        className="product-image-item -small"
                                    >
                                        <div
                                            className={`product-image-container -interactive ${selected ? "-selected" : ""}`}
                                            onClick={() => selectImage(product)}
                                        >
                                            <img
                                                className="image"
                                                alt={product.variation.name || shotIndex}
                                                src={product.variation.urlId}
                                            />
                                        </div>
                                        <h5>
                                            {product.variation.name}
                                            {isDuplicateStaging && addImageDescription(product)}
                                            <span>{product.variation.locale}</span>
                                        </h5>
                                    </ImageListItem>
                                );
                            })}
                        </CompareGallery>
                    </ImageListItem>
                )}

                <ImageListItem className="zoom-item">
                    <CompareGallery
                        title={galleryTitle}
                        selectedProduct={findSelectedImage(imageList, selectedCxhMatch)}
                        shipmentKey={selectedProductCompare.shipmentKey}
                        shipmentProductKey={selectedProductCompare.shipmentProductKey}
                    >
                        {imageList.map((matchedProduct, index) => {
                            const selected = checkCodingImageSelect(matchedProduct) === selectedCxhMatch;

                            return (
                                <ImageListItem
                                    key={checkCodingImageSelect(matchedProduct) || index}
                                    className="product-image-item -small"
                                >
                                    <div
                                        className={`product-image-container -interactive ${selected ? "-selected" : ""}`}
                                        onClick={() => selectImage(null, matchedProduct)}
                                    >
                                        <img
                                            className="image"
                                            alt={matchedProduct.variation.name}
                                            src={matchedProduct.variation.urlId}
                                        />
                                    </div>
                                    <h5>
                                        <span>
                                            {matchedProduct.variation.name}
                                            {isDuplicateStaging && addImageDescription(matchedProduct)}
                                        </span>
                                    </h5>
                                </ImageListItem>
                            );
                        })}
                    </CompareGallery>
                </ImageListItem>
            </ImageDetailsList>
        </div>
    );
};
